import { CAR_ROW, DRAW_DIST, HORIZON_Y, HUD_Y, I0, LAT_CENTER, ROAD_ROWS, SCREEN_H, SCREEN_W } from "../shared/constants";
import { Car, Race, formatTime, machineOf, vclassOf } from "../shared/physics";
import { TrackDef, backdropAt, curvatureAtPos, curveNumberAt, elevationAtPos, minimapPoints, nextCurve, surfaceAtPos } from "../shared/track";
import { drawText, drawTextC, textWidth } from "./font";
import { SpriteSet, SpriteSpec, ROLL_PHASES, Wheel, applyRoll, buildSet, DEFAULT_SPECS, phases, validateSpec } from "../shared/sprites";
import { FRONT_DEFAULTS } from "../shared/frontSprites";

import { PAL } from "./palette";
import { options } from "./options";
import { TEAM_COLORS, NO_TEAM_ME, NO_TEAM_RIVAL, type TeamColor } from "../shared/teamcolors";
import { drawScenery, sceneryTint, type SceneryKind } from "./scenery";
import { drawRoadside } from "./roadside";
export { PAL };
const KZ = DRAW_DIST / (1 / I0 - 1 / 52);
const zOfRow = (i: number) => KZ * (1 / (i + I0) - 1 / 52);

/** Visual boost of the hills (a true perspective projection of such small heights would be nearly invisible on a 48-row road). */
const HILL_GAIN = 2.6;
function rowGeom(track: TrackDef, pos: number, lat: number) {
  const x = new Float32Array(ROAD_ROWS);
  const hw = new Float32Array(ROAD_ROWS);
  const z = new Float32Array(ROAD_ROWS);
  const dy = new Float32Array(ROAD_ROWS); // hills: how far each road row is lifted (negative) or sunk (positive), in pixels
  let shift = 0;
  const shifts = new Float32Array(ROAD_ROWS);
  for (let j = CAR_ROW; j >= 0; j--) {
    shift += (curvatureAtPos(track, pos + zOfRow(j)) * 2 * (CAR_ROW - j)) / CAR_ROW;
    shifts[j] = shift;
  }
  for (let i = 0; i < ROAD_ROWS; i++) {
    z[i] = zOfRow(i);
    hw[i] = (72 * (i + 1)) / ROAD_ROWS;
    x[i] = 80 - 1.5 * (lat - LAT_CENTER) * (i / 47) + shifts[i];
  }
  if (track.hills && track.hills.length) {
    const e0 = elevationAtPos(track, pos);
    for (let i = 0; i < ROAD_ROWS; i++) dy[i] = -(elevationAtPos(track, pos + z[i]) - e0) * HILL_GAIN * ((i + I0) / (CAR_ROW + I0));
  }
  return { x, hw, z, dy };
}

// ---- tyre marks: where a car slides, two thin dark lines stay on the road (for the whole race, laps after laps). Pure display: nothing here touches the physics. ----
const SKID_B = 300; // track units per mark cell
const SKID_OFF = 8; // lateral distance of each rear wheel from the car's centre
let skidRace: object | null = null;
interface SkidTrack { kind: SkidKind; last: number; cells: Map<number, number> } // cell of the lap -> lateral position of the car there
const skids = new Map<number, SkidTrack>();
export type SkidKind = 0 | 1 | 2; // 0 the player, 1 another car, 2 the ghost (drawn dotted, the faintest)
/** Called every frame for a car: records its marks while it slides, and forgets everything when a new race starts. */
export function skidTrace(race: object, lapUnits: number, id: number, kind: SkidKind, pos: number, lat: number, on: boolean) {
  if (race !== skidRace) {
    skidRace = race;
    skids.clear();
  }
  let t = skids.get(id);
  if (!t) skids.set(id, (t = { kind, last: -1, cells: new Map() }));
  if (!on) {
    t.last = -1;
    return;
  }
  const from = t.last >= 0 && pos > t.last && pos - t.last < 8000 ? t.last : pos;
  for (let q = from; q <= pos; q += SKID_B / 2) t.cells.set(Math.floor((((q % lapUnits) + lapUnits) % lapUnits) / SKID_B), lat);
  t.last = pos;
}

// Rear view of a formula car, 25x12, drawn as a mirrored half (12 columns + centre). Original artwork.
const HALF = [
  "..##########|#",
  "..#+++++++++|+",
  "..#...######|#",
  ".####.#@@@@@|@",
  "######.#@@++|o",
  "#o####.#@+oo|o",
  "#o####.##@@@|@",
  "######.#####|#",
  "#o####.@@@@@|@",
  "#o####..####|#",
  "######......|.",
  ".####.......|.",
];
const SPR: string[] = HALF.map((r) => {
  const [h, c] = r.split("|");
  return h + c + [...h].reverse().join("");
});
const SPR_W = 25;
const SPR_H = SPR.length;

/**
 * Rolling wheels: the light tread marks on each wheel are redrawn at a shifted row, so they slide upwards
 * (the rear face of a wheel turning forward moves up). Phase 0 is the original artwork.
 * A wheel's marks repeat every `p` rows, `thick` rows long, starting at row `m0`, between rows y0..y1 (the rim edges stay put).
 */
const STRAIGHT_WHEELS: Wheel[] = [
  { x: 1, mw: 1, y0: 4, y1: 10, m0: 5, p: 3, thick: 2 },
  { x: SPR_W - 2, mw: 1, y0: 4, y1: 10, m0: 5, p: 3, thick: 2 },
];
const SPR_V = Array.from({ length: ROLL_PHASES }, (_, ph) => applyRoll(SPR, STRAIGHT_WHEELS, ph));

/**
 * Car yawed towards the right, seen from behind. level 1 ≈ 10° (front wheel barely showing),
 * level 2 ≈ 20° (front wheel, suspension arm and front wing clearly visible).
 */
function buildYawRight(level: 1 | 2, phase = 0): string[] {
  const W = 27, H = 12;
  const g = Array.from({ length: H }, () => Array(W).fill("."));
  const rect = (x: number, y: number, w: number, h: number, ch: string) => {
    for (let j = y; j < y + h; j++) for (let i = x; i < x + w; i++) if (i >= 0 && i < W && j >= 0 && j < H) g[j][i] = ch;
  };
  const wingR = level === 2 ? 17 : 19; // rear wing narrows as the car turns away
  if (level === 2) {
    rect(21, 6, 5, 5, "#");
    rect(22, 7, 2, 1, "o");
    rect(22, 9, 2, 1, "o");
    rect(19, 8, 2, 1, "@");
    rect(15, 11, 12, 1, "#");
    rect(17, 11, 8, 1, "+");
  } else {
    rect(19, 7, 3, 4, "#");
    rect(20, 8, 1, 1, "o");
    rect(16, 11, 7, 1, "#");
    rect(18, 11, 4, 1, "+");
  }
  rect(1, 0, wingR, 1, "#");
  rect(1, 1, wingR, 1, "+");
  rect(1, 0, 1, 3, "#");
  rect(wingR, 0, 1, 3, "#");
  rect(6, 2, 10, 3, "#");
  rect(7, 3, 8, 2, "@");
  rect(10, 2, 3, 2, "o");
  rect(5, 5, 9, 4, "@");
  rect(8, 6, 5, 1, "+");
  rect(5, 9, 9, 1, "#");
  rect(0, 4, 6, 8, "#");
  rect(1, 6, 2, 1, "o");
  rect(1, 9, 2, 1, "o");
  rect(13, 4, 6, 8, "#");
  rect(14, 6, 2, 1, "o");
  rect(14, 9, 2, 1, "o");
  rect(5, 11, 8, 1, "#");
  const rear = (x: number): Wheel => ({ x, mw: 2, y0: 5, y1: 10, m0: 6, p: 3, thick: 1 });
  const front: Wheel = level === 2 ? { x: 22, mw: 2, y0: 7, y1: 9, m0: 7, p: 2, thick: 1 } : { x: 20, mw: 1, y0: 8, y1: 9, m0: 8, p: 2, thick: 1 };
  return applyRoll(g.map((r) => r.join("")), [rear(1), rear(14), front], phase);
}
const mirror = (rows: string[]) => rows.map((r) => [...r].reverse().join(""));
const YAW_R_V: string[][][] = [[], phases.map((ph) => buildYawRight(1, ph)), phases.map((ph) => buildYawRight(2, ph))];
const YAW_L_V = YAW_R_V.map((v) => v.map((r) => mirror(r)));
const YAW_ANCHOR = 10; // column of the car's centre line in the yawed sprite (right version)

/** Sliding in a curve (20° pose): the body hops and shakes a little, more at speed. Returns a pixel offset. */
export function slideShake(frame: number, seed: number, speed: number): { dx: number; dy: number } {
  const f = frame + seed * 5;
  const hop = [0, 0, 0, 1, 0, 0][Math.floor(f / 3) % 6] * (speed > 250 ? 1 : 1);
  return { dx: [0, 1, 0, -1][Math.floor(f / 4) % 4], dy: -hop };
}
/**
 * Wheel rotation phase of a car: advances with its speed (0 when stopped, capped at 0.5 row per frame so fast wheels
 * shimmer instead of appearing to turn backwards). Kept per car because drawing is stateless.
 */
const rolls = new Map<number, { f: number; a: number }>();
export function wheelPhase(key: number, frame: number, speed: number): number {
  let s = rolls.get(key);
  if (!s || frame < s.f) rolls.set(key, (s = { f: frame, a: 0 })); // new car, or a new race started
  if (frame !== s.f) {
    s.a += Math.min(frame - s.f, 4) * 0.5 * Math.min(1, Math.max(0, speed || 0) / 250);
    s.f = frame;
  }
  return Math.floor(s.a) % ROLL_PHASES;
}
// ---- sprite sets: one per vehicle category (data in shared/sprites.ts; the admin can replace the built-in ones) ----
const FORMULA_SET: SpriteSet = { w: SPR_W, v: SPR_V, yawR: YAW_R_V, yawL: YAW_L_V, anchor: [YAW_ANCHOR, YAW_ANCHOR, YAW_ANCHOR], nominal: [26, 24], front: FRONT_DEFAULTS[0] };
const SETS: SpriteSet[] = [FORMULA_SET, ...DEFAULT_SPECS.slice(1).map((d, i) => buildSet(d!, FRONT_DEFAULTS[i + 1]))];
/** Replaces the built-in sprites of the categories the admin has redrawn (index -> spec). Invalid specs are ignored. */
export function applySprites(over: Record<string, SpriteSpec> | undefined) {
  for (let c = 1; c < SETS.length; c++) {
    const o = over?.[c];
    SETS[c] = buildSet(o && !validateSpec(o) ? o : DEFAULT_SPECS[c]!, FRONT_DEFAULTS[c]);
  }
}
export const spriteWidth = (cls: number, machine: number) => (SETS[cls] ?? FORMULA_SET).nominal[machine === 1 ? 1 : 0];

/** -1 descending, +1 climbing, 0 flat: the slope of the road under a car (hills only; a flat circuit never pitches). */
export function pitchAt(track: TrackDef, pos: number): number {
  if (!track.hills) return 0;
  const de = elevationAtPos(track, pos + 3000) - elevationAtPos(track, pos);
  return de > 1.2 ? 1 : de < -1.2 ? -1 : 0;
}

/** yaw: -2..2 — sign is the turn direction, magnitude 1 ≈ 10°, 2 ≈ 20°. `cls`: vehicle category (see VCLASSES). */
/**
 * "Couleurs" display option (classic green palette only): sky, grass and team liveries in colour. Off: the original four shades.
 * `carColors` maps a car id to its team's colour index (key -1: the player's own car); a car without an entry wears the rival blue.
 */
export const accents = { on: false, carColors: new Map<number, number>() };
const SKY_BANDS = ["#74b4ee", "#88c0f2", "#9ccef6", "#b0dafa"];
const GRASS = ["#8fd070", "#5fae4a"]; // light band, dark band
let carTint: TeamColor | null = null;
const tintOf = (id: number): TeamColor => {
  const c = accents.carColors.get(id);
  return c !== undefined ? TEAM_COLORS[c] ?? NO_TEAM_RIVAL : id === -1 ? NO_TEAM_ME : NO_TEAM_RIVAL;
};
/** Dresses the next drawCarSprite calls in the player's own livery (select cards); null: back to the race logic. */
export function cardTint(on: boolean, teamColor?: number) {
  carTint = on ? (teamColor !== undefined ? TEAM_COLORS[teamColor] ?? NO_TEAM_ME : NO_TEAM_ME) : null;
}
export const cardSky = () => SKY_BANDS[3];
const SURF_COL: Record<string, string> = { snow: "#ffffff", ice: "#a6d6f5", water: "#6fa8dc", gravel: "#b3a58c" };
/** One road row on a special surface: a tint with colours on, a texture (sparkle, ripples, grit) in the four game shades. */
function drawSurfaceRow(ctx: CanvasRenderingContext2D, kind: string, x0: number, x1: number, y: number, bh: number, i: number, wz: number, vk: number) {
  const w = x1 - x0;
  if (accents.on) {
    ctx.fillStyle = SURF_COL[kind];
    ctx.fillRect(x0, y, w, bh);
  }
  const t = Math.floor((wz * vk) / 700);
  const hash = (n: number) => ((Math.imul(n + 1, 2654435761) >>> 0) ^ (Math.imul(n + 7, 40503) >>> 0)) >>> 0;
  if (kind === "water") {
    // ripples: short horizontal dashes
    if ((t + i) % 3 === 0) {
      ctx.fillStyle = accents.on ? "#e8f4ff" : PAL[1];
      const n = Math.max(1, Math.round(w / 22));
      for (let k = 0; k < n; k++) ctx.fillRect(x0 + ((hash(t * 5 + k * 13 + i) % 1000) / 1000) * (w - 4), y, Math.max(2, Math.round(w / 30)), 1);
    }
  } else if (kind === "gravel") {
    ctx.fillStyle = accents.on ? "#6e6250" : PAL[2];
    const n = Math.max(2, Math.round(w / 6));
    for (let k = 0; k < n; k++) ctx.fillRect(x0 + (hash(Math.floor(wz / 380) * 31 + k * 7 + i) % 1000) / 1000 * (w - 1), y, 1, 1);
  } else {
    // snow and ice: scattered glints (ice also gets a dark sheen line down the middle)
    ctx.fillStyle = accents.on ? (kind === "ice" ? "#ffffff" : "#bcd0ea") : PAL[1];
    const n = Math.max(1, Math.round(w / 14));
    for (let k = 0; k < n; k++) ctx.fillRect(x0 + (hash(Math.floor(wz / 900) * 17 + k * 11 + i) % 1000) / 1000 * (w - 1), y, 1, 1);
    if (kind === "ice" && (t + i) % 4 === 0) {
      ctx.fillStyle = accents.on ? "#8fc4e6" : PAL[1];
      ctx.fillRect(x0 + Math.round(w * 0.2), y, Math.round(w * 0.6), 1);
    }
  }
}
export function drawCarSprite(ctx: CanvasRenderingContext2D, cx: number, by: number, w: number, rival: boolean, flash = 0, yaw = 0, roll = 0, cls = 0, front = false, pitch = 0, fade = 0) {
  const set = SETS[cls] ?? FORMULA_SET;
  w = Math.max(5, Math.round(w));
  const lvl = Math.min(2, Math.abs(Math.round(yaw)));
  const sc = w / (front ? set.front[0].length : set.w); // scale relative to the straight sprite (the front view is drawn as wide as the rear one)
  // far cars are too small for the marks (they would only flicker)
  const ph = sc >= 0.5 ? roll : 0;
  const poseOf = (l: number) => (front ? set.front : l === 0 ? set.v[ph] : yaw > 0 ? set.yawR[l][ph] : set.yawL[l][ph]);
  const anchorOf = (l: number, srcW: number) => (front || l === 0 ? srcW / 2 : yaw > 0 ? set.anchor[l] + 0.5 : srcW - set.anchor[l] - 0.5);
  const rows = poseOf(lvl);
  const srcW = rows[0].length, srcH = rows.length;
  const tw = Math.max(5, Math.round(srcW * sc));
  const h = Math.max(3, Math.round(srcH * sc));
  const anchor = anchorOf(lvl, srcW);
  const x0 = Math.round(cx - anchor * sc);
  const y0 = by - h;
  // the pose is about to ease into the next lower one: both are drawn, dithered pixel by pixel (a 2x2 pattern), so the change is a short cross-fade instead of a jump
  const mix = !front && fade > 0 && lvl > 0 ? fade : 0;
  const rowsB = mix ? poseOf(lvl - 1) : rows;
  const srcWB = rowsB[0].length, srcHB = rowsB.length;
  const twB = Math.max(5, Math.round(srcWB * sc));
  const x0B = Math.round(cx - anchorOf(lvl - 1, srcWB) * sc);
  const BAYER = [0.12, 0.62, 0.87, 0.37];
  const lo = mix ? Math.min(0, x0B - x0) : 0, hi = mix ? Math.max(tw, x0B + twB - x0) : tw;
  const line = (y: number, oy: number) => {
    const row = rows[Math.min(srcH - 1, Math.floor(((y + 0.5) * srcH) / h))];
    const rowB = mix ? rowsB[Math.min(srcHB - 1, Math.floor(((y + 0.5) * srcHB) / h))] : row;
    let run = lo, runCh = "";
    for (let x = lo; x <= hi; x++) {
      let ch = "";
      if (x < hi) {
        const cA = x >= 0 && x < tw ? row[Math.min(srcW - 1, Math.floor(((x + 0.5) * srcW) / tw))] : ".";
        if (mix && mix > BAYER[((y & 1) << 1) | (x & 1)]) {
          const xb = x + x0 - x0B;
          ch = xb >= 0 && xb < twB ? rowB[Math.min(srcWB - 1, Math.floor(((xb + 0.5) * srcWB) / twB))] : ".";
        } else ch = cA;
      }
      if (ch !== runCh) {
        if (runCh && runCh !== ".") {
          let c = runCh;
          if (carTint) ctx.fillStyle = c === "#" ? PAL[3] : c === "@" ? carTint.dark : c === "+" ? carTint.light : PAL[0];
          else {
            if (rival) c = c === "@" ? "+" : c === "o" ? "@" : c;
            ctx.fillStyle = c === "#" ? PAL[3] : c === "@" ? PAL[2] : c === "+" ? PAL[1] : PAL[0];
          }
          ctx.fillRect(x0 + run, y0 + y + oy, x - run, 1);
        }
        run = x;
        runCh = ch;
      }
    }
  };
  // on a slope the car's nose lifts (climbing: the upper part rises by a pixel, the car looks taller) or dips (descending: it looks squashed); big enough sprites only
  const k = pitch && !front && h >= 8 ? Math.round(h * 0.45) : 0;
  for (let y = 0; y < h; y++) line(y, y < k ? -pitch : 0);
  if (k && pitch > 0) line(k - 1, 0); // fills the seam the lifted nose leaves
  if (flash) {
    ctx.fillStyle = flash % 4 < 2 ? PAL[3] : PAL[1];
    ctx.fillRect(x0 - 2, y0 - 2, 2, 2);
    ctx.fillRect(x0 + tw, y0 - 3, 2, 2);
  }
}

function hash(n: number) {
  n = Math.imul(n ^ (n >>> 15), 2246822519);
  n = Math.imul(n ^ (n >>> 13), 3266489917);
  return (n ^ (n >>> 16)) >>> 0;
}
function drawTreeLayer(ctx: CanvasRenderingContext2D, scroll: number, spacing: number, lift: number, far: boolean) {
  const base = Math.floor(scroll);
  const shift = ((base % spacing) + spacing) % spacing;
  const first = Math.floor(base / spacing);
  for (let k = -2; k < SCREEN_W / spacing + 2; k++) {
    const id = first + k + (far ? 777 : 0);
    const h = hash(id);
    const r = (far ? 7 : 9) + (h % (far ? 3 : 5));
    const cx = k * spacing - shift + (h % 3);
    const cy = HORIZON_Y - lift;
    blob(ctx, cx, cy, r, PAL[3]); // outline
    blob(ctx, cx, cy, r - 1, far ? PAL[2] : PAL[1]); // body
    if (!far) {
      blob(ctx, cx - Math.floor(r / 3), cy - Math.floor(r / 3), Math.max(1, Math.floor(r / 2)), PAL[0]); // sunlit side
      ctx.fillStyle = PAL[3];
      ctx.fillRect(cx - r + 2, cy - 1, 2 * r - 3, 1); // shadow under the crown
      ctx.fillRect(cx + 1, cy - r + 3, 1, 1);
      ctx.fillRect(cx + 3, cy - r + 4, 1, 1);
    }
  }
}

function drawSmoke(ctx: CanvasRenderingContext2D, x: number, y: number, side: number, frame: number) {
  // tyre smoke puffs trailing on the outside of the curve
  for (let i = 0; i < 4; i++) {
    const ph = (frame + i * 3) % 12;
    const px = x + side * (4 + i * 4 + ph / 3);
    const py = y - 1 - ((ph * 2) >> 2) - (i & 1);
    ctx.fillStyle = ph < 6 ? PAL[2] : PAL[1];
    ctx.fillRect(Math.round(px), Math.round(py), ph < 6 ? 3 : 2, ph < 6 ? 2 : 1);
  }
}

/** Gravel dust kicked up by a sliding rally car: puffs from the rear tyres that blow out on the outside of the slide. */
function drawDust(ctx: CanvasRenderingContext2D, x: number, y: number, dir: number, power: number, frame: number) {
  const n = power > 1.6 ? 8 : 5;
  for (let i = 0; i < n; i++) {
    const ph = (frame + i * 5) % 16;
    const side = i & 1 ? 1 : -1; // one cloud from each rear tyre
    const px = x + side * 10 + dir * (2 + ph * (0.7 + power * 0.25)) + ((i * 7) % 3) - 1;
    const py = y - 1 - ph * 0.55 - (i % 3);
    const sz = ph < 5 ? 2 : ph < 11 ? 3 + (power > 1.6 ? 1 : 0) : 2;
    ctx.fillStyle = ph < 8 ? PAL[2] : PAL[1];
    ctx.fillRect(Math.round(px), Math.round(py), sz, sz > 2 ? 2 : 1);
  }
}

/** Roadside curve warning board: a framed panel with an arrow pointing in the turn direction, on a post. */
function drawSign(ctx: CanvasRenderingContext2D, x: number, by: number, scale: number, dir: number) {
  const w = Math.max(5, Math.round(16 * scale)), h = Math.max(4, Math.round(11 * scale));
  const x0 = Math.round(x - w / 2), y0 = by - h - Math.max(1, Math.round(5 * scale));
  ctx.fillStyle = PAL[3];
  ctx.fillRect(Math.round(x), y0 + h, 1 + (w > 10 ? 1 : 0), by - (y0 + h)); // post
  ctx.fillRect(x0, y0, w, h);
  ctx.fillStyle = PAL[0];
  ctx.fillRect(x0 + 1, y0 + 1, w - 2, h - 2);
  if (w >= 9) {
    ctx.fillStyle = PAL[3];
    const cy = y0 + Math.floor(h / 2), n = Math.max(2, Math.floor((w - 4) / 3));
    for (let i = 0; i < n; i++) {
      const cx = dir > 0 ? x0 + 2 + i * 3 : x0 + w - 3 - i * 3;
      const t = Math.max(1, Math.floor((h - 4) / 2));
      for (let k = 0; k <= t; k++) {
        ctx.fillRect(cx - dir * k, cy - (t - k), 1, 1);
        ctx.fillRect(cx - dir * k, cy + (t - k), 1, 1);
      }
    }
  }
}

function blob(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, c: string) {
  ctx.fillStyle = c;
  for (let dy = -r; dy <= 0; dy++) {
    const half = Math.round(Math.sqrt(r * r - dy * dy));
    ctx.fillRect(cx - half, cy + dy, half * 2, 1);
  }
}

export interface View {
  race: Race;
  me: Car;
  names?: Map<number, string>;
  /** Time trial: a replayed car drawn translucent, and the time gap to it in seconds (positive = ghost ahead). */
  ghost?: Car;
  ghostGap?: number;
  /** Rear-view mirror on/off (default on). */
  mirror?: boolean;
}

/** The curvature that shifts the backdrop eases in and out, so the scenery glides when a curve starts or ends instead of jumping. */
let curvEase = 0, curvFrame = -1;
export function renderRace(ctx: CanvasRenderingContext2D, v: View, frame: number) {
  const { race, me } = v;
  const track = race.track;
  ctx.fillStyle = PAL[0];
  ctx.fillRect(0, 0, SCREEN_W, SCREEN_H);
  sceneryTint.on = accents.on;
  if (accents.on) {
    // sky: four bands, lighter towards the horizon (scenery pieces drawn in the lightest shade stay white on top)
    const bh = Math.ceil(HORIZON_Y / SKY_BANDS.length);
    SKY_BANDS.forEach((c, i) => ((ctx.fillStyle = c), ctx.fillRect(0, i * bh, SCREEN_W, bh)));
  }
  // two layers of bushy trees along the horizon, parallax with distance and curvature
  if (frame !== curvFrame) {
    curvFrame = frame;
    curvEase += (curvatureAtPos(track, me.pos) - curvEase) * 0.07;
  }
  const vk = vclassOf(race.vclass).look ?? 1; // visual-only speed feel of the class (see VClass.look)
  const scroll = (me.pos / 3500) * vk + curvEase * 4;
  const paint = (kind: SceneryKind) => {
    if (!drawScenery(ctx, kind, scroll, frame)) {
      drawTreeLayer(ctx, scroll * 0.6, 14, 3, true);
      drawTreeLayer(ctx, scroll, 17, 0, false);
    }
  };
  // a stage can change scenery along the way: the new backdrop opens from the middle of the horizon as it comes into view
  const bd = backdropAt(track, me.pos + 4500);
  paint(bd.a);
  if (bd.t > 0 && bd.b !== bd.a) {
    const half = Math.round(bd.t * 80);
    ctx.save();
    ctx.beginPath();
    ctx.rect(80 - half, 0, half * 2, HORIZON_Y);
    ctx.clip();
    paint(bd.b);
    ctx.restore();
  }
  ctx.fillStyle = PAL[0];
  ctx.fillRect(0, HORIZON_Y, SCREEN_W, ROAD_ROWS);

  const g = rowGeom(track, me.pos, me.lat);
  const startZ = -me.pos;
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, SCREEN_W, HUD_Y); // hills may lift the road above the horizon, never over the dashboard
  ctx.clip();
  for (let i = 0; i < ROAD_ROWS; i++) {
    // each road row is a band from its own screen line down to the next (nearer) row's, so a slope leaves no gaps; a nearer row that sits higher (a crest) paints over it
    let y = Math.round(HORIZON_Y + i + g.dy[i]);
    if (i === 0 && y > HORIZON_Y) y = HORIZON_Y; // a dip: the plain stretches to the horizon
    const yNext = i < ROAD_ROWS - 1 ? Math.round(HORIZON_Y + i + 1 + g.dy[i + 1]) : HUD_Y;
    const bh = Math.max(1, yNext - y);
    const wz = me.pos + g.z[i];
    const stripe = Math.floor((wz * vk) / 2200) & 1;
    // grass: light bands like the original
    ctx.fillStyle = Math.floor((wz * vk) / 3000) & 1 ? (accents.on ? GRASS[1] : PAL[1]) : accents.on ? GRASS[0] : PAL[0];
    ctx.fillRect(0, y, SCREEN_W, bh);
    const x0 = Math.round(g.x[i] - g.hw[i]);
    const x1 = Math.round(g.x[i] + g.hw[i]);
    const kerb = Math.max(1, Math.round(g.hw[i] * 0.07));
    ctx.fillStyle = stripe ? PAL[3] : PAL[1];
    ctx.fillRect(x0 - kerb, y, kerb, bh);
    ctx.fillRect(x1, y, kerb, bh);
    ctx.fillStyle = PAL[0];
    ctx.fillRect(x0, y, x1 - x0, bh);
    const sf = track.surfaces ? surfaceAtPos(track, wz) : null;
    if (sf) drawSurfaceRow(ctx, sf, x0, x1, y, bh, i, wz, vk);
    if (skids.size && skidRace === race && options.skids && i >= 6) {
      // a far row covers a long stretch of track: look at every mark cell it spans; the others' marks are paler than the player's own
      const zFar = me.pos + (i > 0 ? g.z[i - 1] : g.z[i]);
      const w = i >= 40 ? 2 : 1;
      for (const pass of [false, true]) {
        for (const t of skids.values()) {
          if ((t.kind === 0) !== pass || (!pass && options.skids < 2) || (t.kind === 2 && (i & 1))) continue;
          let lat = -1;
          for (let q = wz, n = 0; q <= zFar + SKID_B && n < 8; q += SKID_B, n++) {
            const l = t.cells.get(Math.floor((((q % track.lapUnits) + track.lapUnits) % track.lapUnits) / SKID_B));
            if (l !== undefined) {
              lat = l;
              break;
            }
          }
          if (lat < 0) continue;
          ctx.fillStyle = pass ? PAL[2] : PAL[1];
          for (const sd of [-SKID_OFF, SKID_OFF]) {
            const xs = Math.round(g.x[i] + (lat + sd - LAT_CENTER) * 1.5 * (i / 47)) - (w >> 1);
            if (xs >= x0 && xs + w <= x1) ctx.fillRect(xs, y, w, bh);
          }
        }
      }
    }
    if (stripe && i > 6) {
      ctx.fillStyle = PAL[1];
      ctx.fillRect(Math.round(g.x[i]), y, 1, bh);
    }
    // start/finish line
    const lapPos = ((wz % track.lapUnits) + track.lapUnits) % track.lapUnits;
    if (track.stage) {
      // a stage: a plain bar at the start, a (wider) chequered band at the finish
      if (wz > 0 && wz < 1100) (ctx.fillStyle = PAL[3]), ctx.fillRect(x0, y, x1 - x0, bh);
      else if (wz >= track.lapUnits && wz < track.lapUnits + 4400) (ctx.fillStyle = Math.floor((wz - track.lapUnits) / 550) & 1 ? PAL[3] : PAL[0]), ctx.fillRect(x0, y, x1 - x0, bh);
    } else if (lapPos < 2200 || (wz < 2200 && wz > 0)) {
      ctx.fillStyle = Math.floor(lapPos / 550) & 1 ? PAL[3] : PAL[0];
      ctx.fillRect(x0, y, x1 - x0, bh);
    }
  }
  void startZ;
  drawRoadside(ctx, g, track, me.pos, vk);
  ctx.restore();

  // all cars (player included) painted far to near by the screen row of their wheels, so overtakes layer correctly
  type Item = { row: number; dz: number; id: number; draw: () => void };
  const items: Item[] = [];
  const labels: { x: number; y: number; text: string }[] = [];
  for (const c of v.ghost ? [...race.cars, v.ghost] : race.cars) {
    if (c === me) continue;
    const isGhost = c === v.ghost;
    const dz = race.relPos(me, c); // (a car a lap away counts as next to you with physics revision 2)
    if (dz < -2500 || dz > DRAW_DIST) continue;
    let row = ROAD_ROWS - 1;
    for (let i = 0; i < ROAD_ROWS; i++) if (g.z[i] <= dz) { row = i; break; }
    // behind the player the car is closer to the camera than the wheels row: extend below the road rows
    if (dz < 0) row = Math.min(ROAD_ROWS + 6, CAR_ROW + Math.round((-dz / 2500) * (ROAD_ROWS + 6 - CAR_ROW)));
    const rr = row;
    const hy = rr < ROAD_ROWS ? g.dy[rr] : 0; // hill lift of the row the car stands on
    const cx = (g.x[Math.min(rr, ROAD_ROWS - 1)] ?? 80) + (c.lat - LAT_CENTER) * 1.5 * (Math.min(rr, ROAD_ROWS - 1) / 47);
    const w = spriteWidth(c.cls, c.machine === 1 ? 1 : 0) * ((rr + 1) / (CAR_ROW + 1));
    const name = v.names?.get(c.id);
    if (name && rr >= 6 && rr < ROAD_ROWS + 2) labels.push({ x: cx, y: HORIZON_Y + rr + hy - Math.round((12 * w) / 25) - 8, text: name });
    items.push({ row: rr, dz, id: c.id, draw: () => {
      if (isGhost) ctx.globalAlpha = 0.5;
      const yw = c.yaw || (c.isAI ? 0 : 2 * curvatureAtPos(track, c.pos));
      const sh = Math.abs(yw) === 2 && c.speed > 150 && rr > 10 ? slideShake(frame, c.id, c.speed) : { dx: 0, dy: 0 };
      carTint = accents.on ? tintOf(c.id) : null;
      drawCarSprite(ctx, cx + sh.dx, HORIZON_Y + rr + hy + sh.dy, w, c.isAI, c.bump, yw, wheelPhase(isGhost ? -2 : c.id, frame, c.speed), c.cls, false, pitchAt(track, c.pos), c.yawFade);
      carTint = null;
      if (c.jetOn && (isGhost || !c.isAI) && w >= 6) {
        // the jet of the ghost and of the other human drivers (online): two flames under the rear, sized with the distance (clearly visible even on the faint ghost)
        const fw = Math.max(1, Math.round(w * 0.11)), fh = Math.max(2, Math.round(w * 0.12));
        const by = Math.round(HORIZON_Y + rr + hy + sh.dy) - 1;
        for (const sx of [-0.27, 0.27]) {
          const fx = Math.round(cx + sh.dx + sx * w - fw / 2);
          ctx.fillStyle = accents.on ? (frame % 2 ? "#ff9a2a" : "#ffd84a") : frame % 2 ? PAL[3] : PAL[2];
          ctx.fillRect(fx, by, fw, fh + ((frame + (sx > 0 ? 1 : 0)) % 3));
          if (accents.on) (ctx.fillStyle = "#ffffff"), ctx.fillRect(fx, by, Math.max(1, fw >> 1), 1);
        }
      }
      ctx.globalAlpha = 1;
    } });
  }
  // roadside warning board ahead of a curve, on the side the road turns towards
  const nc = nextCurve(track, me.pos);
  if (nc && nc.dist > 600 && nc.dist < DRAW_DIST) {
    let row = ROAD_ROWS - 1;
    for (let i = 0; i < ROAD_ROWS; i++) if (g.z[i] <= nc.dist) { row = i; break; }
    const sc = (row + 1) / (CAR_ROW + 1);
    const sx = g.x[row] + nc.dir * (g.hw[row] + 10 * sc);
    items.push({ row, dz: nc.dist, id: -2, draw: () => drawSign(ctx, sx, HORIZON_Y + row + g.dy[row], sc, nc.dir) });
  }
  const kNow = curvatureAtPos(track, me.pos);
  items.push({
    row: CAR_ROW - 1, dz: 0, id: -1,
    draw: () => {
      const sh = Math.abs(me.yaw) === 2 && me.speed > 150 && !me.inWall ? slideShake(frame, 0, me.speed) : { dx: 0, dy: 0 };
      if (me.cls === 1 && !me.inWall && me.speed > 90 && Math.abs(me.slide) > 0.7) drawDust(ctx, 80, HORIZON_Y + CAR_ROW - 1, -Math.sign(me.slide), Math.abs(me.slide), frame);
      carTint = accents.on ? tintOf(-1) : null;
      drawCarSprite(ctx, 80 + sh.dx, HORIZON_Y + CAR_ROW - 1 + sh.dy, spriteWidth(me.cls, 0), false, me.wallFlash, me.yaw, wheelPhase(-1, frame, me.speed), me.cls, false, pitchAt(track, me.pos), me.yawFade);
      carTint = null;
      if (Math.abs(me.yaw) === 2 && me.steer === kNow && me.speed > 190) drawSmoke(ctx, 80 - kNow * 13, HORIZON_Y + CAR_ROW - 1, -kNow, frame);
      if (me.jetOn) {
        const fy = HORIZON_Y + CAR_ROW - 1;
        ctx.fillStyle = accents.on ? (frame % 2 ? "#ff9a2a" : "#ffd84a") : frame % 2 ? PAL[3] : PAL[2];
        ctx.fillRect(74, fy, 3, 2 + (frame % 3));
        ctx.fillRect(83, fy, 3, 2 + ((frame + 1) % 3));
        if (accents.on) (ctx.fillStyle = "#ffffff"), ctx.fillRect(75, fy, 1, 1), ctx.fillRect(84, fy, 1, 1); // white-hot core
      }
    },
  });
  items.sort((a, b) => a.row - b.row || b.dz - a.dz || a.id - b.id);
  for (const it of items) it.draw();
  // name tags of the other human drivers, drawn last so no car hides them
  for (const l of labels) {
    const tw = textWidth(l.text);
    const x = Math.max(2, Math.min(SCREEN_W - tw - 2, Math.round(l.x - tw / 2)));
    const y = Math.max(1, l.y);
    ctx.fillStyle = PAL[0];
    ctx.fillRect(x - 1, y - 1, tw + 2, 7);
    ctx.fillStyle = PAL[3];
    ctx.fillRect(x - 1, y - 1, tw + 2, 1);
    ctx.fillRect(x - 1, y + 5, tw + 2, 1);
    drawText(ctx, l.text, x, y, PAL[3]);
  }

  if (v.mirror !== false && race.phase === "racing" && !me.finished) drawMirror(ctx, v);
  renderHud(ctx, v, frame);
}

/** Rear-view mirror: a small inset at the top centre, shown only while a car is right behind the player. Display only: it never touches the simulation. */
export const MIRROR_DIST = 5000; // track units behind the player (~0.35 s at top speed)
const MIRROR = { x: 58, y: 3, w: 44, h: 19 };
export function mirrorCars(race: Race, me: Car): { car: Car; t: number }[] {
  const out: { car: Car; t: number }[] = [];
  for (const c of race.cars) {
    if (c === me) continue;
    const behind = -race.relPos(me, c);
    if (behind > 0 && behind < MIRROR_DIST) out.push({ car: c, t: 1 - behind / MIRROR_DIST });
  }
  return out.sort((a, b) => a.t - b.t); // far first, near on top
}
function drawMirror(ctx: CanvasRenderingContext2D, v: View) {
  const { race, me } = v;
  const cars = mirrorCars(race, me);
  if (!cars.length) return;
  const { x, y, w, h } = MIRROR;
  const ix = x + 1, iy = y + 1, iw = w - 2, ih = h - 2;
  ctx.fillStyle = PAL[3];
  ctx.fillRect(x, y, w, h);
  ctx.save();
  ctx.beginPath();
  ctx.rect(ix, iy, iw, ih);
  ctx.clip();
  ctx.fillStyle = PAL[0];
  ctx.fillRect(ix, iy, iw, ih);
  const k = 0.3; // mirror pixels per lateral unit
  const depth = (r: number) => 0.3 + 0.7 * (r / (ih - 1)); // perspective factor: 0 = horizon, ih-1 = nearest
  const roadX = (r: number, lat: number) => 80 + (lat - me.lat) * k * depth(r);
  const horizon = 3;
  for (let r = horizon; r < ih; r++) {
    const yy = iy + r;
    const l = Math.round(roadX(r, LAT_CENTER - 57)), rr = Math.round(roadX(r, LAT_CENTER + 57));
    ctx.fillStyle = PAL[1]; // grass either side
    ctx.fillRect(ix, yy, iw, 1);
    ctx.fillStyle = PAL[0];
    ctx.fillRect(l, yy, rr - l, 1);
    ctx.fillStyle = PAL[3]; // kerbs
    ctx.fillRect(l - 1, yy, 1, 1);
    ctx.fillRect(rr, yy, 1, 1);
  }
  for (const { car, t } of cars) {
    const r = Math.round(3 + t * (ih - 4));
    const cw = (5 + t * 11) * (spriteWidth(car.cls, 0) / 26);
    const cx = Math.max(ix + 3, Math.min(ix + iw - 3, roadX(r, car.lat)));
    carTint = accents.on ? tintOf(car.id) : null;
    drawCarSprite(ctx, cx, iy + r + 1, cw, car.isAI, 0, 0, 0, car.cls, true); // seen from the front: they are coming up behind
    carTint = null;
  }
  ctx.restore();
}

function renderHud(ctx: CanvasRenderingContext2D, v: View, frame: number) {
  const { race, me } = v;
  ctx.fillStyle = PAL[3];
  ctx.fillRect(0, HUD_Y, SCREEN_W, SCREEN_H - HUD_Y);
  ctx.fillStyle = PAL[0];
  ctx.fillRect(1, HUD_Y + 1, SCREEN_W - 2, SCREEN_H - HUD_Y - 2);
  // speed
  drawText(ctx, "KM/H", 4, HUD_Y + 4, PAL[2]);
  if (options.speedo === 1) drawDial(ctx, me.speed, me.slip > 3 && !me.finished);
  else drawText(ctx, String(Math.round(me.speed)).padStart(3, " "), 4, HUD_Y + 11, PAL[3], 3);
  // slipstream: "SLIP" lights up while drafting, with the strength of the bonus (km/h) beside the JET label
  if (me.slip > 3 && !me.finished) {
    if (frame % 8 < 6) drawText(ctx, "SLIP", 30, HUD_Y + 4, PAL[3]);
    drawText(ctx, `+${Math.round(me.slip)}`, 24, HUD_Y + 36, PAL[3]);
  }
  // time
  const rf = Math.max(0, race.raceFrame);
  drawText(ctx, "TIME", 60, HUD_Y + 4, PAL[2]);
  drawText(ctx, formatTime(me.finished ? me.finishFrame : rf), 60, HUD_Y + 10, PAL[3], 2);
  // lap + rank
  drawText(ctx, race.track.stage ? `STAGE ${Math.min(100, Math.max(0, Math.floor((me.pos / race.track.lapUnits) * 100)))}%` : `LAP ${race.lapOf(me)}/${race.track.laps}`, 60, HUD_Y + 24, PAL[3]);
  if (v.ghostGap !== undefined) drawText(ctx, `GHOST ${v.ghostGap > 0 ? "+" : v.ghostGap < 0 ? "-" : ""}${Math.abs(v.ghostGap).toFixed(1)}`, 60, HUD_Y + 32, PAL[3]);
  else if (race.cars.length === 1) drawText(ctx, "TIME TRIAL", 60, HUD_Y + 32, PAL[3]);
  else drawText(ctx, `POS ${race.rankOf(me)}/${race.cars.length}`, 60, HUD_Y + 32, PAL[3]);
  // fuel gauge
  const m = machineOf(me);
  drawText(ctx, "JET", 4, HUD_Y + 36, PAL[2]);
  ctx.fillStyle = PAL[2];
  ctx.fillRect(4, HUD_Y + 44, 48, 5);
  ctx.fillStyle = PAL[0];
  ctx.fillRect(5, HUD_Y + 45, 46, 3);
  ctx.fillStyle = me.jetOn && frame % 4 < 2 ? PAL[1] : PAL[3];
  ctx.fillRect(5, HUD_Y + 45, Math.round((46 * me.fuel) / m.fuel), 3);
  // minimap
  const pts = minimapPoints(race.track, 120);
  let minX = 1e9, maxX = -1e9, minY = 1e9, maxY = -1e9;
  for (const p of pts) (minX = Math.min(minX, p.x)), (maxX = Math.max(maxX, p.x)), (minY = Math.min(minY, p.y)), (maxY = Math.max(maxY, p.y));
  const sc = Math.min(34 / (maxX - minX || 1), 40 / (maxY - minY || 1));
  const ox = 122 + (34 - (maxX - minX) * sc) / 2, oy = HUD_Y + 6 + (40 - (maxY - minY) * sc) / 2;
  const mp = (f: number) => {
    const p = pts[Math.floor(((f % 1) + 1) % 1 * (pts.length - 1))];
    return [Math.round(ox + (p.x - minX) * sc), Math.round(oy + (p.y - minY) * sc)];
  };
  ctx.fillStyle = PAL[2];
  for (let i = 0; i < pts.length; i++) {
    const [x, y] = mp(i / (pts.length - 1));
    ctx.fillRect(x, y, 1, 1);
  }
  for (const c of race.cars) {
    const [x, y] = mp(c.pos / race.track.lapUnits);
    ctx.fillStyle = c === me ? PAL[3] : c.isAI ? PAL[1] : PAL[2];
    ctx.fillRect(x - 1, y - 1, c === me ? 3 : 2, c === me ? 3 : 2);
  }
  // the curve the pilot is at (inverted while he is inside it), under the map: helps to follow a race and to tell where a pilot is
  const cn = curveNumberAt(race.track, me.pos);
  if (cn) {
    const label = `C${cn.n}/${cn.total}`;
    const w = label.length * 4 + 1;
    const x0 = 139 - Math.round(w / 2);
    if (cn.inside) {
      ctx.fillStyle = PAL[3];
      ctx.fillRect(x0 - 1, HUD_Y + 47, w + 2, 8);
    }
    drawText(ctx, label, x0, HUD_Y + 49, cn.inside ? PAL[0] : PAL[2]);
  }
  // lap list
  if (!race.track.stage) for (let i = 0; i < me.lapFrames.length; i++) drawText(ctx, `L${i + 1} ${formatTime(me.lapFrames[i])}`, 60, HUD_Y + 40 + i * 6, PAL[2]);
  void drawTextC;
}

/** Dashboard dial (option SPEEDOMETER): a half circle from 0 to 400 km/h with a needle, ticks every 50 and the red zone (above 300) drawn thick. */
function drawDial(ctx: CanvasRenderingContext2D, speed: number, slip: boolean) {
  const cx = 28, cy = HUD_Y + 33, R = 22, MAX = 400;
  const ang = (v: number) => Math.PI * (1 - Math.min(MAX, Math.max(0, v)) / MAX);
  const px = (x: number, y: number, c: string) => { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
  // arc: dotted below 300, solid and doubled above
  for (let v = 0; v <= MAX; v += 4) {
    const a = ang(v), hot = v >= 300;
    px(cx + Math.cos(a) * R, cy - Math.sin(a) * R, hot ? PAL[3] : PAL[2]);
    if (hot) px(cx + Math.cos(a) * (R - 1), cy - Math.sin(a) * (R - 1), PAL[3]);
  }
  // ticks: long every 100, short every 50
  for (let v = 0; v <= MAX; v += 50) {
    const a = ang(v), long = v % 100 === 0, r0 = R - (long ? 5 : 3);
    for (let r = r0; r <= R; r += 0.5) px(cx + Math.cos(a) * r, cy - Math.sin(a) * r, v >= 300 ? PAL[3] : PAL[2]);
  }
  // needle (1 px line) and hub
  const a = ang(speed), len = R - 3;
  for (let r = 0; r <= len; r += 0.5) px(cx + Math.cos(a) * r, cy - Math.sin(a) * r, PAL[3]);
  ctx.fillStyle = PAL[3];
  ctx.fillRect(cx - 1, cy - 1, 3, 3);
  // the number stays, small, under the hub (hidden while the "+N" of the slipstream is shown there)
  if (!slip) drawText(ctx, String(Math.round(speed)).padStart(3, " "), cx - 5, cy + 3, PAL[3]);
}

export function renderCountdown(ctx: CanvasRenderingContext2D, race: Race) {
  const left = race.goFrame - race.frame;
  const lights = Math.min(3, Math.max(0, 3 - Math.floor((left - 40) / 60)));
  const n = left > 220 ? 0 : left > 160 ? 1 : left > 100 ? 2 : 3;
  void lights;
  for (let i = 0; i < 3; i++) {
    ctx.fillStyle = PAL[3];
    ctx.fillRect(56 + i * 18, 6, 14, 14);
    ctx.fillStyle = i < n ? PAL[0] : PAL[2];
    ctx.fillRect(58 + i * 18, 8, 10, 10);
  }
}

/** "WELCOME TO <circuit>": a band slides down from the top during the start lights and slips away when the race starts. */
export function renderIntroBanner(ctx: CanvasRenderingContext2D, race: Race) {
  const f = race.frame, rf = race.raceFrame;
  if (rf > 14) return;
  const H = 18, REST = 24; // band height and resting position (just under the lights)
  let y = REST;
  if (f < 24) y = Math.round(-H + ((REST + H) * (1 - (1 - f / 24) ** 2))); // eases in
  else if (rf > 0) y = Math.round(REST - ((REST + H) * rf) / 14); // leaves at the start
  ctx.fillStyle = PAL[3];
  ctx.fillRect(0, y, SCREEN_W, H);
  ctx.fillStyle = PAL[1];
  ctx.fillRect(0, y + H, SCREEN_W, 1);
  drawTextC(ctx, "WELCOME TO", 80, y + 3, PAL[1]);
  drawTextC(ctx, race.track.name.toUpperCase(), 80, y + 10, PAL[0]);
}
